/**
 * A human label for an external vitamin-API population.
 *
 * The Concept Test audience screen renders `pop.displayName || pop.name`, and the only source of a
 * `displayName` is `clients.json`. So an account with no registry entry — every AI-summit attendee, which is
 * exactly who the scoped population list was built for — chose their audience from a list of raw API keys:
 * "population_breakfast (30 profiles)". This derives the label from the key instead.
 *
 * Derivation, not a lookup table, on purpose: the population list is owned by a separate service and grew from
 * 44 to 53 entries in five days. A table would need editing every time, and a population it had not caught up
 * with would show a raw key — which is the bug being fixed.
 */

/** Populations are named `population_<something>`; the prefix carries no information for a reader. */
const PREFIX = 'population_';

/** `_` is the separator; `.` appears in the admin-only snapshot variants and reads as one too. */
const SEPARATORS = /[_.]+/;

/**
 * Tokens to render upper-case because title case makes them look like typos: "Wp Dog Food", "Knf Bone Broth".
 *
 * An explicit list, NOT a rule like "upper-case any token of three letters or fewer" — that rule turns
 * `population_wp_dog_food` into "WP DOG FOOD". Every entry here occurs in the live list; add to it only from
 * a name that actually exists.
 */
const ACRONYMS = new Set(['wp', 'knf', 'gss']);

const titleCase = (word: string): string => word.charAt(0).toUpperCase() + word.slice(1);

/** `population_breakfast_yogurt` → `Breakfast Yogurt`. Returns the input unchanged if nothing is left to show. */
export const humanizePopulationName = (name: string): string => {
  if (!name) return name;
  const body = name.startsWith(PREFIX) ? name.slice(PREFIX.length) : name;
  const label = body
    .split(SEPARATORS)
    .filter(Boolean)
    .map((word) => (ACRONYMS.has(word.toLowerCase()) ? word.toUpperCase() : titleCase(word)))
    .join(' ');
  // `population_` on its own leaves nothing. A blank option is unpickable and reads as a broken screen, so the
  // key is the lesser evil.
  return label || name;
};

/** What to show for one option: a configured label if there is a real one, otherwise the derived label. */
export const populationLabelFor = (population: { name: string; displayName?: string }): string => {
  const configured = population.displayName?.trim();
  // `clients.json` labels are commercial copy — "Frozen Foods Consumers", not "Amys Frozen" — so they win.
  return configured ? configured : humanizePopulationName(population.name);
};
